import React, { useEffect, useState } from "react";
import {
  Button,
  Form,
  Tabs,
  TabsProps,
  notification,
  Popconfirm,
  Input,
} from "antd";
import { useParams } from "react-router-dom";

import { TbEditCircle } from "react-icons/tb";
import { editMainWebsiteDescription, getMainWebsiteByID } from "../actions";

type Props = {
  fetchData: () => void;
};

interface FormValues {
  description: {
    en: string;
    ar: string;
    fr: string;
  };
}

const MainWebsitesEditDescription: React.FC<Props> = ({ fetchData }) => {
  const [form] = Form.useForm<FormValues>();
  const [api, contextHolder] = notification.useNotification();
  const [isLoading, setIsLoading] = useState(false);
  const { id } = useParams();

  const fetchItem = async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const website = await getMainWebsiteByID(id);
      if (website) {
        form.setFieldsValue({
          description: {
            en: website?.MainInfo?.Description?.En || "",
            ar: website?.MainInfo?.Description?.Ar || "",
            fr: website?.MainInfo?.Description?.Fr || "",
          },
        });
      }
    } catch (err) {
      api.error({
        message: "Failed to fetch website",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setIsLoading(true);

      const success = await editMainWebsiteDescription(id!, values.description);

      if (success) {
        fetchData();
        api.success({
          message: "Description updated successfully",
        });
      }
    } catch (err: any) {
      api.error({
        message: "Failed to update description",
        description:
          err?.response?.errors?.[0]?.message ||
          err?.message ||
          "Unexpected error occurred",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchItem();
  }, [id]);

  const languages = ["en", "ar", "fr"] as const;
  const placeholders: Record<(typeof languages)[number], string> = {
    en: "Enter description",
    ar: "أدخل وصفًا ",
    fr: "Entrez une description",
  };

  const tabItems: TabsProps["items"] = languages.map((lang) => ({
    key: lang,
    label: lang.toUpperCase(),
    children: (
      <Form.Item
        name={["description", lang]}
        label={`Description (${lang.toUpperCase()})`}
      >
        <Input.TextArea
          rows={6}
          className={lang === "ar" ? "text-right" : ""}
          placeholder={placeholders[lang]}
        />
      </Form.Item>
    ),
  }));

  return (
    <Form form={form} layout="vertical">
      {contextHolder}

      <Tabs defaultActiveKey="en" items={tabItems} />

      <Form.Item shouldUpdate>
        {() => (
          <Popconfirm
            title="Are you sure you want to edit the description?"
            onConfirm={handleSubmit}
            okText="Yes"
            cancelText="No"
          >
            <Button
              icon={<TbEditCircle />}
              loading={isLoading}
              type="primary"
              className="w-full"
            >
              {isLoading ? "Saving..." : "Edit Description"}
            </Button>
          </Popconfirm>
        )}
      </Form.Item>
    </Form>
  );
};

export default MainWebsitesEditDescription;
